/**
 * Public tRPC Client for Studio
 *
 * Vanilla tRPC client for calling the Fugue Lab App's public API
 * (intake form submission).
 *
 * The intake form CONTRACT (schema + inferred types) is the single source of
 * truth in `@fugue-lab/types` (the app's public router validates with the same
 * schema). We import only the inferred types here, so studio's typecheck does
 * NOT compile the app's server source — keeping the whole app out of studio's
 * build/CI closure.
 */

import { createTRPCClient, httpBatchLink } from '@trpc/client'
import type { AnyRouter } from '@trpc/server'
import type {
  IntakeFormInput,
  IntakeOrgType,
  IntakeNeed,
  IntakeInvolvement,
  IntakeBudget,
  IntakeTimeline,
  IntakeStyleDirection,
} from '@fugue-lab/types'

// Re-export the shared contract types under the names studio's components use.
export type { IntakeFormInput }
export type OrgType = IntakeOrgType
export type Need = IntakeNeed
export type Involvement = IntakeInvolvement
export type Budget = IntakeBudget
export type Timeline = IntakeTimeline
export type StyleDirection = IntakeStyleDirection

/** Response shape of the public `intake.submit` mutation (studio reads these). */
export interface IntakeSubmitResult {
  success?: boolean
  calendlyUrl?: string
  selfServeUrl?: string
  message?: string
}

/**
 * Base URL for the Fugue Lab App, set per environment via VITE_APP_URL.
 * Falls back to production if not set.
 */
export const APP_URL = import.meta.env.VITE_APP_URL || 'https://fuguelab.app'

function getPublicApiUrl(): string {
  if (import.meta.env.VITE_APP_PUBLIC_API_URL) {
    return import.meta.env.VITE_APP_PUBLIC_API_URL
  }
  return `${APP_URL}/api/public/trpc`
}

interface PublicTrpc {
  intake: {
    submit: {
      mutate: (input: IntakeFormInput) => Promise<IntakeSubmitResult>
    }
  }
}

// The vanilla client is created untyped (AnyRouter — we deliberately don't pull
// in the app's router type) and narrowed to the minimal facade for the one
// procedure studio calls. `client` is typed `unknown` so this is a single,
// honest assertion (no `as unknown as` double-cast).
function asPublicTrpc(client: unknown): PublicTrpc {
  return client as PublicTrpc
}

/**
 * Public tRPC client for Fugue Lab App.
 *
 * Usage:
 *   const result = await publicTrpc.intake.submit.mutate(data);
 */
export const publicTrpc = asPublicTrpc(
  createTRPCClient<AnyRouter>({
    links: [httpBatchLink({ url: getPublicApiUrl() })],
  })
)
